import React from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';

import { formatPrice } from 'common/formatters';
import { INCOME_VERIFICATION_PROVIDER_NAME_MAPPING } from 'common/incomeVerificationConstants';
import {
  SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED,
  SR_APPLICATION_ASSEMBLY_LINE_STATUS_NOT_STARTED,
  SR_APPLICATION_ASSEMBLY_LINE_STATUS_IN_PROGRESS,
} from 'common/applicationAssemblyLineConstants';
import {
  PROOF_OF_INCOME_PAYTSTUBS,
  PROOF_OF_INCOME_PINWHEEL_PAYTSTUBS,
  PROOF_OF_INCOME_OFFER_LETTER,
  PROOF_OF_INCOME_BANK_STATEMENTS,
  PROOF_OF_INCOME_ADDITIONAL_DOCUMENTS,
  PROOF_OF_INCOME_TYPES,
  PROOF_OF_INCOME_TYPE_TO_LABEL_MAPPING,
  PROOF_OF_INCOME_TO_VOIE_STATUS_FIELD_MAPPING,
} from 'common/householdApplication/incomeHelpers';

import isBoolean from 'common/util/isBoolean';

import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrFlex from 'client/components/SrFlex';
import SrButton, {
  SR_BUTTON_THEME_ADOBE_BLUE,
  SR_BUTTON_THEME_ADOBE_WHITE_BLUE_BORDER,
} from 'client/components/SrButton/SrButton';

const BUTTON_TITLE_BY_INCOME_TYPE = {
  [PROOF_OF_INCOME_PAYTSTUBS]: 'View PayStubs',
  [PROOF_OF_INCOME_PINWHEEL_PAYTSTUBS]: 'View PayStubs',
  [PROOF_OF_INCOME_OFFER_LETTER]: 'View Offer Letter',
  [PROOF_OF_INCOME_BANK_STATEMENTS]: 'View Statements',
  [PROOF_OF_INCOME_ADDITIONAL_DOCUMENTS]: 'View Docs',
};
const DOCUMENTS_UPLOADED_TITLE_BY_INCOME_TYPE = {
  [PROOF_OF_INCOME_PAYTSTUBS]: (documents) => `${pluralize('Paystub', documents, true)} uploaded`,
  [PROOF_OF_INCOME_PINWHEEL_PAYTSTUBS]: (documents) =>
    `${pluralize('Paystub', documents, true)} uploaded`,
  [PROOF_OF_INCOME_OFFER_LETTER]: (documents) => `${documents} Offer letter uploaded`,
  [PROOF_OF_INCOME_BANK_STATEMENTS]: (documents) =>
    `${pluralize('Bank Statement', documents, true)} uploaded`, // eslint-disable-line max-len
  [PROOF_OF_INCOME_ADDITIONAL_DOCUMENTS]: (documents) =>
    `${pluralize('Document', documents, true)} uploaded`,
};

const PROOF_OF_INCOME_TYPE_TO_VOIE_SHOW_PARTNER_DOCS_FIELD_MAPPING = {
  [PROOF_OF_INCOME_PAYTSTUBS]: 'paystubsShouldIncludePartnerDocs',
  [PROOF_OF_INCOME_PINWHEEL_PAYTSTUBS]: 'paystubsShouldIncludePartnerDocs',
  [PROOF_OF_INCOME_OFFER_LETTER]: 'offerLetterShouldIncludePartnerDocs',
  [PROOF_OF_INCOME_BANK_STATEMENTS]: 'bankStatementsShouldIncludePartnerDocs',
  [PROOF_OF_INCOME_ADDITIONAL_DOCUMENTS]: 'additionalProofOfIncomePhotos',
};

const PROOF_OF_INCOME_TYPE_TO_VOIE_INCOME_SHOULD_COUNT_FIELD_MAPPING = {
  [PROOF_OF_INCOME_PAYTSTUBS]: 'paystubsShouldCountIncomeSource',
  [PROOF_OF_INCOME_PINWHEEL_PAYTSTUBS]: 'paystubsShouldCountIncomeSource',
  [PROOF_OF_INCOME_OFFER_LETTER]: 'offerLetterShouldCountIncomeSource',
  [PROOF_OF_INCOME_BANK_STATEMENTS]: 'bankStatementsShouldCountIncomeSource',
  [PROOF_OF_INCOME_ADDITIONAL_DOCUMENTS]: 'additionalProofOfIncomePhotos',
};

const getDocumentsStatus = ({
  incomeType,
  assemblyLineVoieStatuses,
  assemblyLineGrossMonthlyIncome,
}) => {
  const voieStatusField = PROOF_OF_INCOME_TO_VOIE_STATUS_FIELD_MAPPING[incomeType];

  switch (incomeType) {
    case PROOF_OF_INCOME_PAYTSTUBS:
    case PROOF_OF_INCOME_PINWHEEL_PAYTSTUBS:
    case PROOF_OF_INCOME_BANK_STATEMENTS: {
      const voieDocuments = assemblyLineVoieStatuses[voieStatusField] || [];
      if (
        !voieDocuments.length ||
        voieDocuments.some((status) => status === SR_APPLICATION_ASSEMBLY_LINE_STATUS_NOT_STARTED)
      ) {
        return SR_APPLICATION_ASSEMBLY_LINE_STATUS_NOT_STARTED;
      }
      return voieDocuments.every(
        (status) => status === SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED,
      )
        ? SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED
        : SR_APPLICATION_ASSEMBLY_LINE_STATUS_IN_PROGRESS;
    }
    case PROOF_OF_INCOME_ADDITIONAL_DOCUMENTS: {
      const voieDocuments = assemblyLineVoieStatuses[voieStatusField] || [];
      if (!voieDocuments.length || voieDocuments.every((status) => status === null)) {
        return SR_APPLICATION_ASSEMBLY_LINE_STATUS_NOT_STARTED;
      }
      return voieDocuments.every(
        (status) => status === SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED || status === null,
      )
        ? SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED
        : SR_APPLICATION_ASSEMBLY_LINE_STATUS_IN_PROGRESS;
    }
    case PROOF_OF_INCOME_OFFER_LETTER: {
      if (assemblyLineVoieStatuses.offerLetter === null) {
        return SR_APPLICATION_ASSEMBLY_LINE_STATUS_NOT_STARTED;
      }

      return assemblyLineGrossMonthlyIncome.offerLetterMonthlyIncome > 0
        ? SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED
        : SR_APPLICATION_ASSEMBLY_LINE_STATUS_IN_PROGRESS;
    }
  }
};

const getDocumentStatusList = ({
  incomeType,
  assemblyLineVoieStatuses,
  assemblyLineGrossMonthlyIncome,
}) => {
  const voieStatusField = PROOF_OF_INCOME_TO_VOIE_STATUS_FIELD_MAPPING[incomeType];

  if (incomeType === PROOF_OF_INCOME_OFFER_LETTER) {
    return assemblyLineGrossMonthlyIncome.offerLetterMonthlyIncome > 0
      ? [SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED]
      : [SR_APPLICATION_ASSEMBLY_LINE_STATUS_IN_PROGRESS];
  }

  const documentStatusList = assemblyLineVoieStatuses[voieStatusField];

  if (Array.isArray(documentStatusList)) {
    return documentStatusList;
  }

  return [documentStatusList];
};

const getDocumentsNeedsReviewText = ({
  incomeType,
  assemblyLineVoie,
  documentStatusList,
  documentsNotStarted,
  incomeShouldCount,
}) => {
  const shouldIncomeCountField =
    PROOF_OF_INCOME_TYPE_TO_VOIE_INCOME_SHOULD_COUNT_FIELD_MAPPING[incomeType];

  if (incomeShouldCount === false) {
    return 'Not used';
  }

  if (documentsNotStarted.length) {
    return [
      documentsNotStarted.length,
      `${pluralize('Doc', documentStatusList.length, true)} Need Review`,
    ].join('/');
  }

  switch (incomeType) {
    case PROOF_OF_INCOME_PAYTSTUBS:
    case PROOF_OF_INCOME_PINWHEEL_PAYTSTUBS:
    case PROOF_OF_INCOME_BANK_STATEMENTS:
    case PROOF_OF_INCOME_OFFER_LETTER: {
      return assemblyLineVoie[shouldIncomeCountField] ? 'Used in Total Income' : 'Not used';
    }
    case PROOF_OF_INCOME_ADDITIONAL_DOCUMENTS: {
      const notReviewed = (assemblyLineVoie.additionalProofOfIncomePhotos || []).filter(
        (status) => status === SR_APPLICATION_ASSEMBLY_LINE_STATUS_NOT_STARTED,
      );

      if (notReviewed.length === 0) {
        return [
          assemblyLineVoie.additionalProofOfIncomePhotos.filter(
            (photo) => photo.shouldCountIncomeSource,
          ).length,
          `${pluralize(
            'Doc',
            assemblyLineVoie.additionalProofOfIncomePhotos.length,
            true,
          )} Used In Income`,
        ].join('/');
      }

      return [
        notReviewed.length,
        `${pluralize(
          'Doc',
          assemblyLineVoie.additionalProofOfIncomePhotos.length,
          true,
        )} Need Review`,
      ].join('/');
    }
  }
};

const getDocumentsSharedToPartnerText = ({ incomeType, assemblyLineVoie, documentsNotStarted }) => {
  const showPartnerDocsField =
    PROOF_OF_INCOME_TYPE_TO_VOIE_SHOW_PARTNER_DOCS_FIELD_MAPPING[incomeType];
  if (documentsNotStarted.length) {
    return '';
  }

  switch (incomeType) {
    case PROOF_OF_INCOME_PAYTSTUBS:
    case PROOF_OF_INCOME_PINWHEEL_PAYTSTUBS:
    case PROOF_OF_INCOME_OFFER_LETTER:
    case PROOF_OF_INCOME_BANK_STATEMENTS:
      return assemblyLineVoie[showPartnerDocsField] ? 'Shown to Partner' : 'Not shown to Partner';
    case PROOF_OF_INCOME_ADDITIONAL_DOCUMENTS: {
      const docsShownToPartnerCount = assemblyLineVoie[showPartnerDocsField].filter(
        (photo) => photo.shouldIncludePartnerDocs,
      ).length;

      if (docsShownToPartnerCount === 0) {
        return 'Not shown to Partner';
      }

      return [
        docsShownToPartnerCount,
        `${pluralize('Doc', assemblyLineVoie[showPartnerDocsField].length, true)} Shown To Partner`,
      ].join('/');
    }
  }
};

const SrIncomeVerificationCardAssemblyLine = ({
  amount,
  onView,
  incomeType,
  documentsCount,
  voieStartedDate,
  incomeShouldCount,
  incomeVerificationProvider,
  assemblyLineVoie,
  assemblyLineVoieStatuses,
  assemblyLineGrossMonthlyIncome,
}) => {
  const documentStatusList = getDocumentStatusList({
    incomeType,
    assemblyLineVoieStatuses,
    assemblyLineGrossMonthlyIncome,
  });

  const documentsNotStarted = documentStatusList.filter(
    (status) => status === SR_APPLICATION_ASSEMBLY_LINE_STATUS_NOT_STARTED,
  );

  const documentsStatus = getDocumentsStatus({
    incomeType,
    assemblyLineVoieStatuses,
    assemblyLineGrossMonthlyIncome,
  });

  const cornerText = getDocumentsNeedsReviewText({
    incomeType,
    assemblyLineVoie,
    documentsStatus,
    documentStatusList,
    documentsNotStarted,
    incomeShouldCount,
  });

  const documentsSharedToPartnerText = getDocumentsSharedToPartnerText({
    incomeType,
    assemblyLineVoie,
    documentsStatus,
    documentStatusList,
    documentsNotStarted,
    incomeShouldCount,
  });

  let title = PROOF_OF_INCOME_TYPE_TO_LABEL_MAPPING[incomeType];
  const buttonTitle = BUTTON_TITLE_BY_INCOME_TYPE[incomeType];
  const amountFormatted = amount ? formatPrice(amount) : '$-';
  const documentsUploaded = DOCUMENTS_UPLOADED_TITLE_BY_INCOME_TYPE[incomeType](documentsCount);

  let borderColor;
  let amountColor;
  let buttonTheme;
  let cornerTextColor;

  // If the income should not count, we should show the card as if it was not started.
  if (isBoolean(isBoolean) && !incomeShouldCount) {
    borderColor = '1px solid #D4D6D8';
    amountColor = 'greyMonochromeInactive';
    buttonTheme = SR_BUTTON_THEME_ADOBE_WHITE_BLUE_BORDER;
    cornerTextColor = 'grey400';
  } else if (documentsStatus === SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED) {
    borderColor = '1px solid #00A238';
    amountColor = 'green';
    buttonTheme = SR_BUTTON_THEME_ADOBE_BLUE;
    cornerTextColor = 'green';
  } else {
    borderColor = '1px solid #D4D6D8';
    amountColor = 'fontBlueDark';
    buttonTheme = SR_BUTTON_THEME_ADOBE_BLUE;
    cornerTextColor = 'grey400';

    if (documentsStatus === SR_APPLICATION_ASSEMBLY_LINE_STATUS_IN_PROGRESS) {
      borderColor = '1px solid #E7711B';
    }
  }

  const voieStartedDateFormatted = voieStartedDate
    ? moment(voieStartedDate).format('MMM D, YYYY')
    : '';
  const startedDateDaysFromNow = moment().diff(moment(voieStartedDate), 'days');

  if (incomeVerificationProvider) {
    title = INCOME_VERIFICATION_PROVIDER_NAME_MAPPING[incomeVerificationProvider];
  }

  return (
    <SrFlex padding="16px" direction="col" borderRadius="7px" border={borderColor}>
      <SrBox marginBottom="4px">
        <SrFlex justify="between">
          <SrText font="greycliff" weight="700" fontSize="12px" lineHeight="18px">
            {title}
            <SrText
              display="block"
              font="greycliff"
              weight="800"
              fontSize="28px"
              lineHeight="34px"
              letterSpacing="1px"
              color={amountColor}
            >
              {amountFormatted}
            </SrText>
          </SrText>
          <SrText
            font="greycliff"
            weight="500"
            fontSize="12px"
            color={cornerTextColor}
            lineHeight="18px"
          >
            {cornerText}
            <br />
            {documentsSharedToPartnerText}
          </SrText>
        </SrFlex>
      </SrBox>
      <SrText font="greycliff" weight="500" fontSize="12px" lineHeight="18px">
        Total Monthly Income
      </SrText>
      {!incomeVerificationProvider && (
        <SrBox marginTop="8px">
          <SrText font="greycliff" weight="400" fontSize="10px" lineHeight="12px">
            {documentsUploaded}
          </SrText>
        </SrBox>
      )}
      <SrBox marginTop="2px">
        <SrText font="greycliff" weight="400" fontSize="10px" lineHeight="12px">
          {voieStartedDate && (
            <>
              Start date:
              <SrText font="greycliff" weight="700" fontSize="10px" lineHeight="12px">
                {voieStartedDateFormatted} ({startedDateDaysFromNow}d ago)
              </SrText>
            </>
          )}
        </SrText>
      </SrBox>
      <SrBox marginTop="16px">
        <SrButton theme={buttonTheme} onClick={onView} padding="0">
          {buttonTitle}
        </SrButton>
      </SrBox>
    </SrFlex>
  );
};

SrIncomeVerificationCardAssemblyLine.propTypes = {
  onView: PropTypes.func.isRequired,
  incomeType: PropTypes.oneOf(PROOF_OF_INCOME_TYPES).isRequired,
  amount: PropTypes.number.isRequired,
  documentsCount: PropTypes.number.isRequired,
  incomeShouldCount: PropTypes.bool,
  incomeVerificationProvider: PropTypes.string,
  voieStartedDate: PropTypes.string,
  assemblyLineVoie: PropTypes.object.isRequired,
};

SrIncomeVerificationCardAssemblyLine.displayName = 'SrIncomeVerificationCardAssemblyLine';

export default SrIncomeVerificationCardAssemblyLine;
